import { v4 as uuid } from 'uuid'
import { StateStorage } from 'zustand/middleware'
import { createStore } from '../.'
import { Contact, ContactCreateUpdate } from '../../interfaces/common'
import { addressToChain } from '../../utils/addressMatcher'

interface UserContactStore {
  contacts: Contact[]
  setContacts: (contacts: Contact[]) => void
  getContactById: (id: string) => Contact | undefined
  getContactsByAddress: (address: string) => Contact[]
  addContact: (attrs: ContactCreateUpdate) => string
  updateContact: (id: string, attrs: Partial<ContactCreateUpdate>) => void
  removeContact: (id: string) => void
}

export const createContactsStore = (storage: StateStorage) =>
  createStore<UserContactStore>(
    storage,
    (set, get) => ({
      contacts: [],
      setContacts: (contacts: Contact[]) => {
        set({ contacts })
      },
      getContactById(id) {
        const { contacts } = get()

        return contacts.find((c) => c.id === id)
      },
      getContactsByAddress(address) {
        const { contacts } = get()

        return contacts.filter((c) =>
          c.addresses.map((a) => a.address.toLowerCase()).includes(address.toLowerCase())
        )
      },
      addContact(contact) {
        const id = uuid()
        set(({ contacts }) => ({ contacts: [...contacts, { id, ...contact }] }))
        return id
      },
      updateContact(id, attrs) {
        set(({ contacts }) => ({
          contacts: contacts
            .map((c) => {
              if (c.id === id) {
                return { ...c, ...attrs }
              }

              return c
            })
            .filter((c) => !!c),
        }))
      },
      removeContact(id) {
        const { contacts } = get()
        set({ contacts: contacts.filter((c) => c.id !== id) })
      },
    }),
    {
      persist: {
        name: 'plug.contacts.user',
        version: 1,
        migrate: (persistedStateUnchecked) => {
          const persistedState = persistedStateUnchecked as UserContactStore

          return {
            ...persistedState,
            contacts: persistedState.contacts.map((c) => {
              // @ts-ignore
              const address = c.address as string
              let chain = addressToChain(address) || ''

              return {
                ...c,
                id: address,
                addresses: [{ chain: chain, address }],
              }
            }),
          }
        },
      },
    }
  )
